import { useCallback, useMemo, useRef, useState, type HTMLAttributes } from 'react';

/**
 * Colunas de tabela com largura arrastável.
 *
 * O Ant Design v6 não traz isto pronto - o v5 tinha um exemplo com
 * `react-resizable`, que saiu na v6. Aqui é feito na mão, com eventos de
 * ponteiro: são umas poucas dezenas de linhas, não trazem dependência nova para
 * um problema pequeno, e `setPointerCapture` resolve de graça o caso que quebra
 * as implementações com `mousemove` no documento - arrastar rápido, sair da
 * janela e soltar o botão lá fora, que sem captura deixa a coluna colada no
 * ponteiro para sempre.
 *
 * **A largura fica salva.** Sem isso, ajustar a coluna do enunciado e navegar
 * para uma questão desfaria o ajuste na volta, e o recurso seria um brinquedo:
 * quem arruma a tabela arruma uma vez, não a cada visita.
 */

/** Nada abaixo disto: coluna de largura zero não dá para pegar de volta. */
const LARGURA_MINIMA = 60;

interface LargurasSalvas {
  versao: number;
  larguras: Record<string, number>;
}

/*
 * A versão invalida o que estiver salvo quando as colunas mudarem. Sem ela,
 * uma coluna removida deixaria a largura órfã ocupando espaço no armazenamento
 * para sempre, e uma renomeada leria a largura de outra.
 */
const VERSAO = 1;

function ler(chave: string): Record<string, number> {
  try {
    const cru = localStorage.getItem(chave);
    if (!cru) return {};
    const salvo = JSON.parse(cru) as LargurasSalvas;
    return salvo.versao === VERSAO ? salvo.larguras : {};
  } catch {
    // Armazenamento bloqueado ou JSON corrompido: a tabela abre no padrão. É
    // preferência de layout, não dado - não vale quebrar a tela por ela.
    return {};
  }
}

function salvar(chave: string, larguras: Record<string, number>) {
  try {
    localStorage.setItem(chave, JSON.stringify({ versao: VERSAO, larguras }));
  } catch {
    // Idem: em aba anônima com armazenamento negado, a largura vale só para
    // esta sessão.
  }
}

export function useLarguraDasColunas(chave: string, padroes: Record<string, number>) {
  const [larguras, setLarguras] = useState<Record<string, number>>(() => ler(chave));

  /*
   * O arrasto é aplicado no próximo quadro, e não a cada evento de ponteiro.
   *
   * Mudar o estado aqui redesenha a tabela inteira - o Ant Design não consegue
   * saber o que cada `render` de coluna usa, então redesenha tudo por
   * segurança. Com cem linhas na tela, um evento por pixel arrastado trava o
   * arrasto; um por quadro não.
   */
  const quadro = useRef<number | null>(null);
  /*
   * A referência é a dona da medida; o estado só existe para redesenhar.
   *
   * Duas razões. Durante o arrasto o estado anda um quadro atrás de propósito,
   * e quem solta o ponteiro logo depois de mexer - o caso normal, não o raro -
   * gravaria a medida anterior, perdendo os últimos pixels ao recarregar. E
   * espelhar o estado na referência a cada render atropelaria um arrasto em
   * curso, se qualquer outra coisa da tela redesenhasse no meio dele.
   */
  const atuais = useRef(larguras);

  /** Muda a medida nos dois lugares, e grava se for para gravar. */
  const aplicar = useCallback(
    (proximas: Record<string, number>, gravar: boolean) => {
      atuais.current = proximas;
      setLarguras(proximas);
      if (gravar) salvar(chave, proximas);
    },
    [chave],
  );

  const redimensionar = useCallback((coluna: string, largura: number) => {
    atuais.current = {
      ...atuais.current,
      [coluna]: Math.max(LARGURA_MINIMA, Math.round(largura)),
    };
    if (quadro.current !== null) return;

    quadro.current = requestAnimationFrame(() => {
      quadro.current = null;
      setLarguras(atuais.current);
    });
  }, []);

  /** Só ao soltar: gravar a cada quadro seria escrita em disco por pixel. */
  const guardar = useCallback(() => salvar(chave, atuais.current), [chave]);

  const restaurar = useCallback(() => aplicar({}, true), [aplicar]);

  /** Devolve uma coluna ao padrão, apagando a medida em vez de fixá-la. */
  const restaurarUma = useCallback(
    (nome: string) => {
      const { [nome]: _descartada, ...resto } = atuais.current;
      aplicar(resto, true);
    },
    [aplicar],
  );

  /** As props de largura e arrasto de uma coluna, prontas para espalhar. */
  const coluna = useCallback(
    (nome: string) => ({
      width: larguras[nome] ?? padroes[nome],
      onHeaderCell: () =>
        ({
          largura: larguras[nome] ?? padroes[nome],
          aoRedimensionar: (valor: number) => redimensionar(nome, valor),
          aoSoltar: guardar,
          aoRestaurar: () => restaurarUma(nome),
        }) as never,
    }),
    [larguras, padroes, redimensionar, guardar, restaurarUma],
  );

  /*
   * A soma das colunas, que é a largura da tabela.
   *
   * Precisa ser **número**, e não `max-content`. Com `max-content` o navegador
   * dimensiona pelo conteúdo: o nome de prova mais longo estica a coluna além
   * da largura pedida, e a reticência nunca aparece porque nunca falta espaço.
   * Encolher a coluna vira pedido ignorado. Com a soma explícita, o
   * `table-layout: fixed` respeita cada largura e o texto corta.
   */
  const total = useMemo(
    () => Object.keys(padroes).reduce((soma, nome) => soma + (larguras[nome] ?? padroes[nome]!), 0),
    [larguras, padroes],
  );

  return { coluna, total, restaurar, personalizada: Object.keys(larguras).length > 0 };
}

interface PropsDoCabecalho extends HTMLAttributes<HTMLTableCellElement> {
  largura?: number;
  aoRedimensionar?: (largura: number) => void;
  aoSoltar?: () => void;
  aoRestaurar?: () => void;
}

/**
 * O `<th>` com a alça de arrasto na borda direita.
 *
 * Vai para `components.header.cell` da tabela. As colunas que não passam
 * `aoRedimensionar` (a da seleção, por exemplo) caem no cabeçalho normal.
 */
export function CabecalhoRedimensionavel({
  largura,
  aoRedimensionar,
  aoSoltar,
  aoRestaurar,
  children,
  ...resto
}: PropsDoCabecalho) {
  const inicio = useRef<{ x: number; largura: number } | null>(null);
  const [ativa, setAtiva] = useState(false);

  if (!aoRedimensionar || largura === undefined) {
    return <th {...resto}>{children}</th>;
  }

  return (
    <th {...resto} style={{ ...resto.style, position: 'relative' }}>
      {children}
      <div
        // A alça é larga o bastante para pegar com o ponteiro sem mira, mas
        // fica metade para fora da célula, para não comer o texto do título.
        style={{
          position: 'absolute',
          top: 0,
          right: -5,
          width: 10,
          height: '100%',
          cursor: 'col-resize',
          touchAction: 'none',
          zIndex: 1,
          userSelect: 'none',
        }}
        onPointerDown={(evento) => {
          /*
           * Sem `preventDefault` aqui, de propósito. Ele suprime os eventos de
           * mouse derivados do ponteiro, e o clique duplo que devolve a coluna
           * ao padrão é um deles - o atalho sumiria em parte dos navegadores,
           * silenciosamente. O arrasto não precisa dele: a captura do ponteiro
           * já garante os eventos, e `user-select: none` já barra a seleção de
           * texto que era o outro motivo para chamá-lo.
           */
          evento.stopPropagation();
          evento.currentTarget.setPointerCapture(evento.pointerId);
          inicio.current = { x: evento.clientX, largura };
          setAtiva(true);
        }}
        onPointerMove={(evento) => {
          if (!inicio.current) return;
          aoRedimensionar(inicio.current.largura + (evento.clientX - inicio.current.x));
        }}
        onPointerUp={(evento) => {
          if (!inicio.current) return;
          evento.currentTarget.releasePointerCapture(evento.pointerId);
          inicio.current = null;
          setAtiva(false);
          aoSoltar?.();
        }}
        /*
         * O clique é barrado à parte: ele nasce depois do `pointerup` e sobe
         * sozinho até o `th`, que ordena a tabela. Sem isto, terminar um
         * arrasto reordenaria a lista inteira - e a coluna recém-medida sairia
         * do lugar como prêmio.
         */
        onClick={(evento) => evento.stopPropagation()}
        onPointerEnter={() => setAtiva(true)}
        onPointerLeave={() => {
          // Sair no meio do arrasto não apaga a divisa: com a captura ativa o
          // ponteiro sai da alça e o arrasto continua.
          if (!inicio.current) setAtiva(false);
        }}
        // Clique duplo na divisa devolve a coluna ao padrão. É a convenção das
        // planilhas, e evita caçar o botão de restaurar por causa de uma
        // coluna só - que, aliás, devolveria todas.
        onDoubleClick={(evento) => {
          evento.stopPropagation();
          aoRestaurar?.();
        }}
      >
        {/* A divisa só aparece com o ponteiro em cima ou durante o arrasto:
            desenhada em toda coluna, viraria uma grade que a tabela não tem. */}
        <div
          style={{
            position: 'absolute',
            insetBlock: 0,
            left: 4,
            width: 2,
            background: 'currentColor',
            opacity: ativa ? 0.45 : 0,
            transition: 'opacity .15s',
          }}
        />
      </div>
    </th>
  );
}
